import { useEffect, useState } from 'react';

import { useExportEvents, BaseExportEvent, ExportPhase } from '@/api/hooks/useExportEvents';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface Props {
  /** Callback to trigger the download when complete */
  onDownload: () => void;
  /** Error message if creation failed */
  error?: string;
  /** Whether the export already exists and is ready */
  exportReady: boolean;
  /** Name of the item being exported (for display) */
  itemName: string;
  /** Optional: Additional description for the ready state */
  readyDescription?: string;
  /** Archive creation progress percentage (0-100) */
  archiveProgress?: number;
  /** Total number of documents being processed */
  totalDocuments?: number;
  /** Number of documents processed so far */
  processedDocuments?: number;
  /** Optional: Custom text for download button (defaults to "Download") */
  downloadButtonText?: string;
  /** Optional: Callback to retry on error (shows "Try Again" button) */
  onRetry?: () => void;
  /** Current phase of the export: 'processing' or 'uploading' */
  phase?: ExportPhase;
  /** Upload progress percentage (0-100) when phase is 'uploading' */
  uploadProgress?: number;
}

export function ExportProgressContent({
  onDownload,
  error,
  exportReady,
  itemName,
  readyDescription,
  archiveProgress = 0,
  totalDocuments = 0,
  processedDocuments = 0,
  downloadButtonText = 'Download',
  onRetry,
  phase = 'processing',
  uploadProgress = 0,
}: Props) {
  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50/80 px-3 py-2 text-red-600">
          <Icon className="lucide--alert-triangle text-red-500" />
          Error creating export
        </div>

        <p className="mt-6">Sorry, something went wrong trying to create your export.</p>
        <div className="text-center text-gray-600">{error}</div>

        {onRetry && (
          <Button onClick={onRetry} className="w-full">
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Ready state
  if (exportReady) {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="mt-4 flex items-center gap-3 text-base font-medium">
          <Icon className="lucide--check-circle text-teal-500" />
          <div className="text-gray-900">Export ready for download</div>
        </div>

        <div className="mt-3 text-center text-balance text-gray-600">
          {readyDescription || `${itemName} with source documents has been packaged.`}
          {totalDocuments > 0 && (
            <div className="text-medium mt-2 text-sm text-gray-500">
              {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <div className="mt-4 flex w-full gap-2">
          <Button variant="primary" onClick={onDownload} className="w-full">
            {downloadButtonText}
          </Button>
        </div>
      </div>
    );
  }

  // Creating state - show progress bar with phase-specific content
  const isUploading = phase === 'uploading';
  const isInitialising = phase === 'processing' && totalDocuments === 0;
  const displayProgress = isUploading ? uploadProgress : archiveProgress;

  // Initialising state - waiting for first server event
  if (isInitialising) {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="my-6 flex items-center gap-3">
          <Icon className="lucide--loader-circle animate-spin text-gray-500" />
          <div className="text-sm text-gray-600">Initialising export. This may take a few minutes.</div>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <Icon className="lucide--mail mt-px" />
          You will receive an email when the export is ready.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="-mx-1 flex gap-2 rounded-md border bg-gray-50 p-3 px-4 text-sm text-gray-600">
        <Icon className="lucide--info mt-1 text-gray-500" />
        This may take a few minutes depending on the number and size of documents.
      </div>

      <div className="my-6 w-full px-3">
        <div className="mb-2.5 flex justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Icon className="lucide--loader-circle animate-spin" />
            {isUploading ? (
              <div className="text-sm text-gray-500">Finalising download</div>
            ) : (
              <div className="text-sm text-gray-500">
                Processing <strong>{processedDocuments}</strong> of <strong>{totalDocuments}</strong> document
                {totalDocuments !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          <span>{Math.round(displayProgress)}%</span>
        </div>

        <div className="h-2.5 w-full rounded-full bg-gray-200">
          <div
            className="h-2.5 rounded-full bg-teal-400 transition-all duration-300"
            style={{ width: `${displayProgress}%` }}
          ></div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <Icon className="lucide--mail mt-px" />
        You will receive an email when the export is ready.
      </div>
    </div>
  );
}

/**
 * Configuration for export endpoints
 */
export interface ExportConfig {
  /** Base endpoint for progress SSE */
  progressEndpoint: string;
  /** Export ID appended as query param to filter SSE events */
  exportId: string | null;
}

/**
 * Props for GenericExportProgressModal
 */
interface GenericExportProgressModalProps<TEvent extends BaseExportEvent> {
  // Modal control
  isOpen: boolean;
  onClose: () => void;

  // Export configuration
  exportConfig: ExportConfig;

  // Display & behavior
  itemName: string;
  onDownload: () => Promise<void>;
  isCreating: boolean;
  error?: string;

  // Optional customization
  readyDescription?: string;
  downloadButtonText?: string;
  onRetry?: () => void;
}

export const GenericExportProgressModal = <TEvent extends BaseExportEvent>({
  isOpen,
  onClose,
  exportConfig,
  itemName,
  onDownload,
  isCreating,
  error,
  readyDescription,
  downloadButtonText,
  onRetry,
}: GenericExportProgressModalProps<TEvent>) => {
  const [exportReady, setExportReady] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [processedDocuments, setProcessedDocuments] = useState(0);
  const [phase, setPhase] = useState<ExportPhase>('processing');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sseError, setSseError] = useState<string>();

  // Reset state when a new export starts
  useEffect(() => {
    if (isCreating) {
      setExportReady(false);
      setArchiveProgress(0);
      setTotalDocuments(0);
      setProcessedDocuments(0);
      setPhase('processing');
      setUploadProgress(0);
      setSseError(undefined);
    }
  }, [isCreating]);

  useExportEvents<TEvent>({
    progressEndpoint: `${exportConfig.progressEndpoint}?exportId=${exportConfig.exportId}`,
    onEvent: (event) => {
      if (event.type.includes('progress')) {
        setArchiveProgress(event.archiveProgress || 0);
        if (event.totalDocuments) setTotalDocuments(event.totalDocuments);
        if (event.processedDocuments !== undefined) setProcessedDocuments(event.processedDocuments);
        if (event.phase) setPhase(event.phase);
        if (event.uploadProgress !== undefined) setUploadProgress(event.uploadProgress);
      } else if (event.type.includes('complete')) {
        setArchiveProgress(100);
        setExportReady(true);
      } else if (event.type.includes('error')) {
        setSseError(event.error || 'An unexpected error occurred while creating the export.');
      }
    },
    enabled: !!exportConfig.exportId && !exportReady && !sseError,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="focus:outline-none">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" /> Exporting {itemName}
          </DialogTitle>
          <DialogDescription>Please wait while your export is in progress</DialogDescription>
        </DialogHeader>

        <ExportProgressContent
          onDownload={onDownload}
          error={error || sseError}
          exportReady={exportReady}
          itemName={itemName}
          readyDescription={readyDescription}
          archiveProgress={archiveProgress}
          totalDocuments={totalDocuments}
          processedDocuments={processedDocuments}
          downloadButtonText={downloadButtonText}
          onRetry={onRetry}
          phase={phase}
          uploadProgress={uploadProgress}
        />
      </DialogContent>
    </Dialog>
  );
};
